import {
    type Selector,
    createSelector,
    createSelectorCreator,
    defaultMemoize,
} from "reselect";
import {values, sortBy, isEqual} from "lodash";
import {orderBy} from "lodash/fp";
import type {GlobalSportState, SportState} from "@atg-sport-shared/sportsbook-types-root";
import type {CombinedGroup, GroupMap} from "@atg-sport-shared/sportsbook-types-group";

const orderBySport = orderBy(["sortOrder", "name"], ["asc", "asc"]);
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const getProps = (_: GlobalSportState, id: number) => id;

export const groupSelector: (state: GlobalSportState) => GroupMap =
    createDeepEqualSelector(
        (state: GlobalSportState) => state.sportsbook,
        (sportsbook: SportState) => (sportsbook ? sportsbook.groups : {}),
    );

export const groupValuesSelector: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(groupSelector, (groups): Array<CombinedGroup> => values(groups));

export const getSports: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(
        groupValuesSelector,
        (groupsValues): Array<CombinedGroup> =>
            groupsValues.filter((group) => !group.parentId),
    );

export const getSortedSports: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(getSports, orderBySport) as Selector<
        GlobalSportState,
        CombinedGroup[]
    >;

export const getPopularSports: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(
        getSports,
        (sports): Array<CombinedGroup> =>
            sortBy(
                sports.filter((sport: CombinedGroup) => sport.sortOrder),
                ["sortOrder"],
            ),
    );

export const getLiveSports: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(
        getSports,
        (sports): Array<CombinedGroup> =>
            sports.filter((sport: CombinedGroup) => sport.hasLive),
    );

export const getSortedLiveSports: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(getLiveSports, orderBySport) as Selector<
        GlobalSportState,
        CombinedGroup[]
    >;

export const getStartingSoonSports: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(
        getSports,
        (sports): Array<CombinedGroup> =>
            sports.filter((sport: CombinedGroup) => sport.hasStartingSoon),
    );

export const getSortedStartingSoonSports: (
    state: GlobalSportState,
) => Array<CombinedGroup> = createSelector(
    getStartingSoonSports,
    orderBySport,
) as Selector<GlobalSportState, CombinedGroup[]>;

export const getLeagues: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(
        groupValuesSelector,
        (groupsValues): Array<CombinedGroup> =>
            groupsValues.filter((group) => group.parentId !== null && group.leaf),
    );

export const getPopularLeagues: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(
        getLeagues,
        (leagues): Array<CombinedGroup> =>
            sortBy(
                leagues.filter((league: CombinedGroup) => league.leagueSortOrder),
                ["leagueSortOrder"],
            ),
    );

export const getRegions: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(
        groupValuesSelector,
        (groupsValues): Array<CombinedGroup> =>
            groupsValues.filter((group) => group.parentId !== null && !group.leaf),
    );

export const getHighlights: (state: GlobalSportState) => Array<CombinedGroup> =
    createSelector(
        groupValuesSelector,
        (groupsValues): Array<CombinedGroup> =>
            groupsValues.filter((group: CombinedGroup) => group.isHighlighted),
    );

export const makeGroupSelector: () => (
    state: GlobalSportState,
    id: number,
) => CombinedGroup = () =>
    createSelector(groupSelector, getProps, (groups, id) => groups[String(id)]);
